// =============================================================================
// useAlertsDue — how many HSE alerts this person still has to acknowledge
// (mig 413, 3 Oct 2026). The twin of usePoliciesDue: one number feeding the
// field home nudge, the tile and the phone-menu badge, so they all agree.
// =============================================================================
// Reads v_hse_alert_recipients — the same view the manager's matrix uses —
// filtered to the signed-in employee. ack_outstanding is already "published,
// needs an ack, no ack yet"; urgentOutstanding splits out the ones that are
// overdue or due within 24 h so the home card can shout about them.
// A login with no employee (the crew tablet) always gets zero.
// =============================================================================
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export type AlertsDue = { total: number; urgent: number; overdue: number };

export function useAlertsDue(employeeId: string | null | undefined): AlertsDue {
  const { data } = useQuery({
    queryKey: ["my_alerts_due", employeeId],
    enabled: !!employeeId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("v_hse_alert_recipients")
        .select("alert_id, severity, ack_overdue")
        .eq("employee_id", employeeId!)
        .eq("ack_outstanding", true);
      if (error) throw error;
      const rows = (data ?? []) as Array<{ severity: string; ack_overdue: boolean }>;
      return {
        total: rows.length,
        urgent: rows.filter(r => r.severity === "urgent").length,
        overdue: rows.filter(r => r.ack_overdue).length,
      } as AlertsDue;
    },
  });
  return data ?? { total: 0, urgent: 0, overdue: 0 };
}
